import React, { FC, useMemo } from 'react';

type Variant = 'default' | 'white' | 'secondary';

export type ButtonProps = {
  /**
   * Label of the button
   */
  label?: string;
  /**
   * Variant to define the button style
   */
  variant?: Variant;
  /**
   * Click action of button
   */
  onClick?(): void;
  /**
   * Button Type
   */
  type?: 'button' | 'submit' | 'reset';
  /**
   * Flag to disable button
   */
  disabled?: boolean;
  /**
   * Child elements
   */
  children?: React.ReactNode;
};

const BASE_BUTTON_CLASSES =
  'font-sans font-medium text-base text-center rounded-full border focus:outline-none min-h-[48px] px-12 leading-5';
const DEFAULT_BUTTON_CLASSES = `${BASE_BUTTON_CLASSES} text-white border-action bg-action hover:bg-hover focus:bg-hover hover:border-hover focus:border-hover disabled:text-gray-500 disabled:border-gray-200 disabled:bg-gray-200`;
const WHITE_BUTTON_CLASSES = `${BASE_BUTTON_CLASSES} border-white text-action bg-white hover:text-white focus:text-white hover:bg-hover focus:bg-hover hover:border-hover focus:border-hover disabled:text-gray-500 disabled:border-gray-200 disabled:bg-gray-200`;
const SECONDARY_BUTTON_CLASSES = `${BASE_BUTTON_CLASSES} border-gray-500 text-action bg-white hover:border-hover focus:border-hover hover:text-hover focus:text-hover disabled:text-gray-400 disabled:border-gray-200`;

export const Button: FC<ButtonProps> = ({
  label,
  variant = 'default',
  onClick,
  disabled,
  type,
  children,
}: ButtonProps) => {
  const className = useMemo(() => {
    let name = '';
    switch (variant) {
      case 'white':
        name += WHITE_BUTTON_CLASSES;
        break;
      case 'secondary':
        name += SECONDARY_BUTTON_CLASSES;
        break;
      default:
        name += DEFAULT_BUTTON_CLASSES;
        break;
    }
    if (disabled) {
      name += ' cursor-default';
    }
    return name;
  }, [variant, disabled]);
  return (
    <button
      onClick={onClick}
      className={className}
      disabled={disabled}
      type={type}
    >
      {label}
      {children}
    </button>
  );
};
